import * as React from "react"
import { X, Check, ChevronsUpDown } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export interface MultiSelectOption {
  label: string
  value: string
  disabled?: boolean
  color?: string
  title?: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  onSearchChange?: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  loading?: boolean
  singleSelect?: boolean
}

export function MultiSelect({
  options,
  selected,
  onChange,
  onSearchChange,
  placeholder = "Select items...",
  className,
  disabled = false,
  loading = false,
  singleSelect = false,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const debounceTimer = React.useRef<NodeJS.Timeout | null>(null)

  React.useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  const handleSearchChange = React.useCallback((value: string) => {
    if (!onSearchChange) return;
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }
    debounceTimer.current = setTimeout(() => {
      onSearchChange(value)
    }, 500)
  }, [onSearchChange])

  // Guard: API may return null / a string on edit mode — always use an array
  const safeSelected: string[] = Array.isArray(selected) ? selected : []

  const handleUnselect = (item: string) => {
    onChange(safeSelected.filter((i) => i !== item))
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-auto min-h-10 py-1.5 px-3 bg-card hover:bg-card border border-border hover:border-secondary/40 rounded-xl text-sm font-medium transition-all shadow-sm",
            className
          )}
          onClick={() => !disabled && setOpen(!open)}
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-1.5 items-center min-w-0 flex-1 overflow-hidden pr-2">
            {safeSelected.length > 0 ? (
              safeSelected.map((item) => {
                const option = options.find((o) => o.value === item)
                return (
                  <Badge
                    variant="secondary"
                    key={item}
                    className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 text-xs font-semibold rounded-lg bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15 transition-colors shrink-0"
                  >
                    <span className="truncate max-w-[130px] sm:max-w-[170px] md:max-w-[200px]">
                      {option?.label || item}
                    </span>
                    <button
                      type="button"
                      className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20 text-primary/80 hover:text-primary transition-colors shrink-0"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleUnselect(item)
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleUnselect(item)
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )
              })
            ) : (
              <span className="text-muted-foreground font-normal text-sm truncate">{placeholder}</span>
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-auto" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[100]" align="start">
        <Command className="w-full" shouldFilter={!onSearchChange}>
          <CommandInput
            placeholder="Search..."
            className="h-10"
            onValueChange={onSearchChange ? handleSearchChange : undefined}
          />
          <CommandList>
            {loading ? (
              <div className="py-6 text-center text-sm flex items-center justify-center gap-2 text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                Searching...
              </div>
            ) : (
              <CommandGroup className="p-1">
                {options.length > 0 && !loading && !singleSelect && (
                  <CommandItem
                    onSelect={() => {
                      if (safeSelected.length === options.length) {
                        onChange([])
                      } else {
                        onChange(options.map(o => o.value))
                      }
                    }}
                    className="cursor-pointer font-bold border-b mb-1 rounded-md"
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary transition-colors",
                        safeSelected.length === options.length
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50"
                      )}
                    >
                      {safeSelected.length === options.length && <Check className="h-3 w-3" />}
                    </div>
                    <span>{safeSelected.length === options.length ? "Deselect All" : "Select All"}</span>
                  </CommandItem>
                )}
                {options.length === 0 && !loading && (
                  <CommandEmpty className="py-6 text-center text-sm">No items found.</CommandEmpty>
                )}

                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={`${option.label} ${option.value}`}
                    disabled={option.disabled}
                    onSelect={() => {
                      if (option.disabled) return;
                      if (singleSelect) {
                        onChange(safeSelected.includes(option.value) ? [] : [option.value])
                        setOpen(false)
                      } else {
                        onChange(
                          safeSelected.includes(option.value)
                            ? safeSelected.filter((item) => item !== option.value)
                            : [...safeSelected, option.value]
                        )
                      }
                    }}
                    className={cn(
                      "rounded-md mb-0.5",
                      option.disabled ? "opacity-50 cursor-not-allowed grayscale-[0.5]" : "cursor-pointer"
                    )}
                    title={option.title}
                  >
                    {!singleSelect && (
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary transition-colors",
                          safeSelected.includes(option.value)
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50"
                        )}
                      >
                        {safeSelected.includes(option.value) && <Check className="h-3 w-3" />}
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-1 overflow-hidden">
                      {option.color && (
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: option.color }}
                        />
                      )}
                      <span
                        className={cn(
                          "flex-1 truncate",
                          singleSelect && safeSelected.includes(option.value) && "font-bold text-primary"
                        )}
                        style={option.color ? { color: option.color } : {}}
                      >
                        {option.label}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
